import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CnpjTextField from "../../../../../components/react-form/CnpjTextField";
import { benefitsOptions } from "../data/benefitsOptions";

export default function HealthPlanOperatorFieldset({ index, disabled, onRemove, canRemove }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const ns = `healthPlanOperators.${index}`;
  const e = errors.healthPlanOperators?.[index] ?? {};

  const hasCoparticipation = useWatch({ control, name: `${ns}.hasCoparticipation` });
  const coparticipationRule = useWatch({ control, name: `${ns}.coparticipationRule` });
  const beneficiariesOutsideEmployees = useWatch({ control, name: `${ns}.beneficiariesOutsideEmployees` });

  return (
    <Box
      component="fieldset"
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, mb: 1, "& legend": { px: 1 } }}
    >
      <Box component="legend" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary" }}>
          Operadora {index + 1}
        </Typography>
        {canRemove && !disabled && (
          <IconButton onClick={onRemove} color="error" size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Controller
            name={`${ns}.name`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Nome da operadora"
                size="small"
                fullWidth
                error={!!e.name}
                helperText={e.name?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.cnpj`}
            control={control}
            render={({ field }) => (
              <CnpjTextField
                {...field}
                disabled={disabled}
                label="Dados do fornecedor - CNPJ"
                size="small"
                fullWidth
                error={!!e.cnpj}
                helperText={e.cnpj?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.ansCode`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Cód. ANS"
                size="small"
                fullWidth
                error={!!e.ansCode}
                helperText={e.ansCode?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${ns}.type`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!e.type}>
                <InputLabel id={`hp-op-${index}-type-label`}>Tipo Plano</InputLabel>
                <Select
                  {...field}
                  disabled={disabled}
                  labelId={`hp-op-${index}-type-label`}
                  label="Tipo Plano"
                >
                  {benefitsOptions.healthPlanOperator1Type.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{e.type?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.companyCost`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Custo Empresa (%)"
                size="small"
                fullWidth
                error={!!e.companyCost}
                helperText={e.companyCost?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.employeeCost`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Custo Colaborador (%)"
                size="small"
                fullWidth
                error={!!e.employeeCost}
                helperText={e.employeeCost?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.dependentCost`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Custo Dependente (%)"
                size="small"
                fullWidth
                error={!!e.dependentCost}
                helperText={e.dependentCost?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.aggregateCost`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Custo Agregado (%)"
                size="small"
                fullWidth
                error={!!e.aggregateCost}
                helperText={e.aggregateCost?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${ns}.hasCoparticipation`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!e.hasCoparticipation}>
                <InputLabel id={`hp-op-${index}-has-copart-label`}>Possui Coparticipação</InputLabel>
                <Select
                  {...field}
                  disabled={disabled}
                  labelId={`hp-op-${index}-has-copart-label`}
                  label="Possui Coparticipação"
                >
                  {benefitsOptions.healthPlanOperator1HasCoparticipation.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{e.hasCoparticipation?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasCoparticipation === "1" && (
          <>
            <Grid item xs={12} md={6}>
              <Controller
                name={`${ns}.companyCoparticipationCost`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={disabled}
                    label="Custo Empresa Coparticipação (%)"
                    size="small"
                    fullWidth
                    error={!!e.companyCoparticipationCost}
                    helperText={e.companyCoparticipationCost?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={`${ns}.employeeCoparticipationCost`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={disabled}
                    label="Custo Colaborador Coparticipação (%)"
                    size="small"
                    fullWidth
                    error={!!e.employeeCoparticipationCost}
                    helperText={e.employeeCoparticipationCost?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={`${ns}.dependentCoparticipationCost`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={disabled}
                    label="Custo Dependente Coparticipação (%)"
                    size="small"
                    fullWidth
                    error={!!e.dependentCoparticipationCost}
                    helperText={e.dependentCoparticipationCost?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name={`${ns}.aggregateCoparticipationCost`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={disabled}
                    label="Custo Agregado Coparticipação (%)"
                    size="small"
                    fullWidth
                    error={!!e.aggregateCoparticipationCost}
                    helperText={e.aggregateCoparticipationCost?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={coparticipationRule === "1" ? 6 : 12}>
                  <Controller
                    name={`${ns}.coparticipationRule`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!e.coparticipationRule}>
                        <InputLabel id={`hp-op-${index}-copart-rule-label`}>Tabela de coparticipação enviada?</InputLabel>
                        <Select
                          {...field}
                          disabled={disabled}
                          labelId={`hp-op-${index}-copart-rule-label`}
                          label="Tabela de coparticipação enviada?"
                        >
                          {benefitsOptions.healthPlanOperator1CoparticipationRule.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{e.coparticipationRule?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                {coparticipationRule === "1" && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name={`${ns}.coparticipationRuleDescription`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          disabled={disabled}
                          label="Descreva a Regra de Negócio"
                          size="small"
                          fullWidth
                          error={!!e.coparticipationRuleDescription}
                          helperText={e.coparticipationRuleDescription?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={beneficiariesOutsideEmployees === "1" ? 6 : 12}>
              <Controller
                name={`${ns}.beneficiariesOutsideEmployees`}
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!e.beneficiariesOutsideEmployees}>
                    <InputLabel id={`hp-op-${index}-beneficiaries-label`}>Beneficiários fora do quadro de empregados?</InputLabel>
                    <Select
                      {...field}
                      disabled={disabled}
                      labelId={`hp-op-${index}-beneficiaries-label`}
                      label="Beneficiários fora do quadro de empregados?"
                    >
                      {benefitsOptions.healthPlanOperator1BeneficiariesOutsideEmployees.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{e.beneficiariesOutsideEmployees?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            {beneficiariesOutsideEmployees === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name={`${ns}.beneficiariesOutsideEmployeesDescription`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={disabled}
                      label="Descreva a Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!e.beneficiariesOutsideEmployeesDescription}
                      helperText={e.beneficiariesOutsideEmployeesDescription?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.reportReceptionDate`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Data de recebimento do relatório (dia do mês)"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 31 }}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, "");
                  const num = parseInt(val, 10);
                  if (val === "" || (num >= 1 && num <= 31)) {
                    field.onChange(val);
                  }
                }}
                error={!!e.reportReceptionDate}
                helperText={e.reportReceptionDate?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name={`${ns}.adjustmentDate`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={disabled}
                label="Data de reajuste (dia do mês)"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 31 }}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, "");
                  const num = parseInt(val, 10);
                  if (val === "" || (num >= 1 && num <= 31)) {
                    field.onChange(val);
                  }
                }}
                error={!!e.adjustmentDate}
                helperText={e.adjustmentDate?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
